import { current, isDraft } from 'immer';
import { DraftConfiguration, DraftError } from '@/types';
import { isEmbroideryMode } from '@/utils/params';
import { getCurrentSizeOfConfiguration } from '@/model/configuration/getCurrentSizeOfConfiguration';
import { validateConfiguration } from '@/model/configuration/validateConfiguration';
import { validateEmbroideryFrame } from '@/model/configuration/validateEmbroideryFrame';
import { getConfiguration } from '@/model/draft/getConfiguration';
import { getConfigurations } from '@/model/draft/getConfigurations';
import { getProductType } from '@/model/draft/getProductType';
import { setValidationAction } from '@/components/draft/draftState/actions/setValidationAction';
import { DraftValidator } from '@/components/draft/draftState/types';

export const validateDraft: DraftValidator = (draft, previousDraft) => {
  const configurationEntries = getConfigurations(draft);
  if (configurationEntries.length === 0) {
    return;
  }

  const productType = getProductType(draft);
  const changedConfigurations: DraftConfiguration[] = [];
  const errors: Record<number, DraftError> = {};

  configurationEntries.forEach((configuration) => {
    // if it is not a draft, it is new and should be validated
    if (
      !isDraft(configuration) ||
      current(configuration) !== getConfiguration(previousDraft, configuration.cid)
    ) {
      changedConfigurations.push(configuration);

      errors[configuration.cid] = validateConfiguration(
        configuration,
        getCurrentSizeOfConfiguration(configuration),
        productType
      );
    }
  });

  if (changedConfigurations.length === 0) {
    return;
  }

  if (isEmbroideryMode()) {
    const configurationList = configurationEntries.map((configuration) => configuration);
    new Set(changedConfigurations.map((config) => config.printArea)).forEach((printArea) => {
      const configurationsOnView = configurationList.filter(
        (configuration) => configuration.printArea.id === printArea.id
      );
      const [isValid, configurationIds] = validateEmbroideryFrame(
        configurationsOnView,
        productType,
        printArea
      );
      configurationIds.forEach((configurationId) => {
        const configurationErrors = errors[configurationId] || (errors[configurationId] = {});
        if (!configurationErrors.boundary && !configurationErrors.maxBound) {
          configurationErrors.embroideryBoundingBox = !isValid;
        }
      });
    });
  }

  setValidationAction(errors)(draft);
};
